import 'react-image-crop/dist/ReactCrop.css';

import { Excalidraw, exportToBlob } from '@excalidraw/excalidraw';
import { ExcalidrawElement, FileId } from '@excalidraw/excalidraw/types/element/types';
import {
  AppState,
  BinaryFileData,
  BinaryFiles,
  DataURL,
  ExcalidrawImperativeAPI,
} from '@excalidraw/excalidraw/types/types';
import { Modal, Tooltip } from 'antd';
import { DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import AddMediaSvg from '../../../assets/icons/AddMediaSvg';
import EraserSvg from '../../../assets/icons/EraserSvg';
import StickyNoteSvg from '../../../assets/icons/StickyNoteSvg';
import theme from '../../../assets/theme';
import { useGameBackgroundMediaFileUpload } from '../../../hooks/useGameBackgroundMediaFileUpload';
import { useGameDrawingBackgroundFiles } from '../../../hooks/useGameDrawingBackgroundFiles';
import { useGameExcalidrawJSONUpload } from '../../../hooks/useGameExcalidrawJSONUpload';
import { useGameMediaBackgroundJSONFile } from '../../../hooks/useGameMediaBackgroundJSONFile';
import { useGameMediaFileUploader } from '../../../hooks/useGameMediaFileUploader';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { RoundMediaDrawingFile } from '../../../services/backendService/types';
import { getFiles, SUPPORTED_IMAGE_MIMETYPES } from '../../../utils/fileDropUtils';
import { generateUniqueId } from '../../../utils/general';
import { createStickyNote, getMediaAspectRatio } from '../../../utils/templateUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import ConditionalWrapper from '../../_atoms/ConditionalWrapper';
import FileDropComponent from '../../_atoms/FileDropComponent/FileDropComponent';
import { IconWrapper } from '../../_atoms/IconWrapper';
import Loader from '../../Loader';
import {
  ExistingDrawing,
  Slide,
  SlideElement,
  SlideElementImageType,
} from '../../SlideComponents/slide.types';
import { CenteredContainer } from '../../styled/CenteredContainer';
import GameMediaRenderer from '../GameMediaRenderer';
import MaxSizeMediaContent from '../MaxSizeMediaContent';
import SuspensePostHydration from '../SuspensePostHydration';

const CURIPOD_SYSTEM_NODE_PREFIX = 'curipod_v1';

const CustomToolbarButtonWrapper = styled.div<{ $active?: boolean }>`
  padding: 5px;
  border-radius: 5px;
  transition: 0.2s linear;
  ${({ $active }) => $active && 'background: rgba(0,0,0,0.1);'}
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const blobToBase64 = (blob: Blob) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

function shouldShowDrawingCanvas(
  existingDrawing?: ExistingDrawing,
  elements?: ExcalidrawElement[],
  files?: BinaryFiles,
) {
  if (!existingDrawing) return true;
  if (!existingDrawing.backgroundDrawingFileId) {
    return false;
  }
  if (
    existingDrawing.backgroundDrawingFileId &&
    existingDrawing.backgroundDrawingConfigFiles
  ) {
    return !!elements && !!files;
  }
  if (
    existingDrawing.backgroundDrawingFileId &&
    !existingDrawing.backgroundDrawingConfigFiles
  ) {
    return !!elements;
  }
  if (
    !existingDrawing.backgroundDrawingFileId &&
    existingDrawing.backgroundDrawingConfigFiles
  ) {
    return !!files;
  }
  return false;
}

export const DrawingBuilder = ({
  onDrawingCompleted,
  existingDrawing,
  isUploading,
  onCancel,
}: {
  existingDrawing?: ExistingDrawing;
  isUploading?: boolean;
  onCancel: () => void;
  onDrawingCompleted: (
    imgData?: string,
    elements?: readonly ExcalidrawElement[],
    files?: BinaryFiles,
    api?: ExcalidrawImperativeAPI,
  ) => void;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const excalidrawApiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  // Must be a function that is called by excalidraw when the canvas has been mounted.
  const excalidrawRef = useCallback((excalidrawApi: ExcalidrawImperativeAPI) => {
    excalidrawApiRef.current = excalidrawApi;
  }, []);

  const { data: existingElements } = useGameMediaBackgroundJSONFile(
    existingDrawing?.backgroundDrawingFileId,
  );

  const { data: backgroundDrawingFiles } = useGameDrawingBackgroundFiles(
    existingDrawing?.backgroundDrawingConfigFiles,
  );

  const [activeTool, setActiveTool] = useState<string>();

  const handleChange = (
    _: readonly ExcalidrawElement[],
    appState: AppState,
    _1: BinaryFiles,
  ) => {
    if (appState.activeTool.type !== activeTool) {
      setActiveTool(appState.activeTool.type);
    }
  };

  const handleSelectErasor = () => {
    const appState = excalidrawApiRef.current?.getAppState();
    const activeTool = excalidrawApiRef.current?.getAppState().activeTool;
    if (appState && activeTool) {
      const newActiveTool = {
        ...activeTool,
        lastActiveToolBeforeEraser:
          activeTool.lastActiveToolBeforeEraser === undefined
            ? activeTool.lastActiveToolBeforeEraser
            : activeTool.lastActiveToolBeforeEraser,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: 'eraser' as any,
        customType: null,
      };
      excalidrawApiRef.current?.updateScene({
        appState: {
          // Must deselect to prevent excalidraw to switch back to selector as active tool
          selectedElementIds: {},
          selectedGroupIds: {},
          activeTool: newActiveTool,
        },
      });
    }
  };

  const handleCreateStickyNote = () => {
    const existingElements = excalidrawApiRef.current?.getSceneElements();
    const state = excalidrawApiRef.current?.getAppState();
    if (state) {
      const { scrollX, scrollY, height, width, zoom } = state;
      const stickyNoteElements = createStickyNote({
        scrollX,
        scrollY,
        height,
        width,
        zoom: zoom.value,
        defaultText: t('Add text'),
      });

      excalidrawApiRef.current?.updateScene({
        appState: {
          activeTool: {
            customType: null,
            lastActiveToolBeforeEraser: { type: 'selection', customType: null },
            locked: false,
            type: 'selection',
          },
        },
        elements: existingElements
          ? [...existingElements, ...stickyNoteElements]
          : stickyNoteElements,
      });
    }
  };

  const handleBackgroundSaved = async () => {
    metrics.logEvent('Creator.Media.BackgroundSave.Click');
    const excalidrawApi = excalidrawApiRef.current;
    if (excalidrawApi) {
      const blob = await exportToBlob({
        elements: excalidrawApi
          .getSceneElements()
          .filter(el => !el.id.startsWith(CURIPOD_SYSTEM_NODE_PREFIX)),
        appState: excalidrawApi.getAppState(),
        files: excalidrawApi.getFiles(),
        mimeType: 'image/jpeg',
        maxWidthOrHeight: 1920,
      });

      if (blob) {
        const base64 = await blobToBase64(blob);
        onDrawingCompleted(
          base64 as string,
          excalidrawApi
            .getSceneElements()
            .filter(el => !el.id.startsWith(CURIPOD_SYSTEM_NODE_PREFIX)),
          excalidrawApi.getFiles(),
          excalidrawApi,
        );
      }
    }
  };

  const handleEscapePress = () => {
    metrics.logEvent('Creator.BackgroundDrawing.Escape');
    handleBackgroundSaved();
  };

  const showDrawingCanvas = shouldShowDrawingCanvas(
    existingDrawing,
    existingElements,
    backgroundDrawingFiles,
  );

  const unLockedExistingElements = useMemo(() => {
    const elements = existingElements
      ? existingElements.map(e => ({ ...e, locked: false }))
      : [];

    /* 
      The hack is to add use Excalidraw's
      automatical color shortcuts which selects 5 colors used on the canvas and creates shortcuts.
      This hack creates a few rectangle elements with opacity 0, so they will not be seen.
      But excalidraw will help the user select that color.
      The main idea is to add a few missing colors: white, brown and yellow
       */
    const dot: ExcalidrawElement = {
      opacity: 0, // <--- hidden trick!
      angle: 0,
      width: 20,
      height: 20,
      backgroundColor: 'transparent',
      strokeColor: 'transparent',
      boundElements: null,
      fillStyle: 'hachure',
      groupIds: [],
      id: 'cMfloYsDRItX17_aA5uhm',
      isDeleted: false,
      link: null,
      locked: true,
      roughness: 1,
      seed: 634756961,
      strokeSharpness: 'sharp',
      strokeStyle: 'solid',
      strokeWidth: 1,
      type: 'rectangle',
      updated: 1656833500831,
      version: 3,
      versionNonce: 2081228111,
      x: 460,
      y: 160,
    };

    const predefinedColor = [
      theme.colors.white,
      '#000000',
      '#964b00',
      theme.colors.yellow,
      '#fab005',
      '#ced4da',
      '#c92a2a',
    ];

    return [
      ...elements,
      ...predefinedColor.map(color => ({
        ...dot,
        backgroundColor: color,
        strokeColor: color,
        id: `${CURIPOD_SYSTEM_NODE_PREFIX}_${dot.id}_${color}`,
      })),
    ];
  }, [existingElements]);

  return (
    <>
      <DoneContainer>
        <Button
          loading={isUploading}
          width="120px"
          type="primary"
          styles={{ border: 'none' }}
          height="38px"
          onClick={() => handleBackgroundSaved()}
        >
          {t('Save')}
        </Button>
        <Divider />
        <CustomToolbarButtonWrapper onClick={() => handleCreateStickyNote()}>
          <Tooltip title={t('Sticky note')}>
            <IconWrapper height="32px" width="32px">
              <StickyNoteSvg />
            </IconWrapper>
          </Tooltip>
        </CustomToolbarButtonWrapper>
        <CustomToolbarButtonWrapper
          $active={activeTool === 'eraser'}
          onClick={() => handleSelectErasor()}
        >
          <Tooltip title={t('Eraser')}>
            <IconWrapper height="32px" width="32px">
              <EraserSvg />
            </IconWrapper>
          </Tooltip>
        </CustomToolbarButtonWrapper>
        <Divider />
        <Button
          width="120px"
          type="ghost"
          styles={{ border: `1px ${theme.colors.grey} solid` }}
          height="38px"
          onClick={() => onCancel()}
        >
          {t('Cancel')}
        </Button>
      </DoneContainer>
      {!showDrawingCanvas && (
        <CenteredContainer>
          <Loader />
        </CenteredContainer>
      )}
      {showDrawingCanvas && (
        <Excalidraw
          ref={excalidrawRef}
          viewModeEnabled={false}
          zenModeEnabled={false}
          gridModeEnabled={true}
          onChange={handleChange}
          initialData={{
            elements: unLockedExistingElements,
            files: backgroundDrawingFiles,
          }}
          renderFooter={() => <></>}
          UIOptions={{
            canvasActions: {
              saveAsImage: false,
              changeViewBackgroundColor: false,
              clearCanvas: false,
              export: false,
              saveToActiveFile: false,
              loadScene: false,
            },
          }}
        />
      )}
      <Hotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </>
  );
};

const Divider = styled.div`
  height: 100%;
  width: 1px;
  background: #e4e4e4;
  border-radius: 200px;
  margin-right: 5px;
  margin-left: 5px;
`;

const EditImageOverlay = ({
  onEdit,
  onDelete,
  className,
}: {
  onEdit: () => void;
  onDelete: () => void;
  className?: string;
}) => {
  const { t } = useTranslation('Host');
  return (
    <EditMediaOverlay>
      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            bottom: 15,
            right: 15,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <RemoveContainer className={className} onClick={onEdit}>
            {t('Edit')}
          </RemoveContainer>
          <RemoveContainer
            style={{ backgroundColor: theme.colors.red, color: 'white' }}
            onClick={onDelete}
          >
            {t('Remove')}
          </RemoveContainer>
        </div>
      </div>
    </EditMediaOverlay>
  );
};

const RemoveContainer = styled.button`
  background: white;
  color: black;
  border-radius: 5px;
  border: none;
  width: fit-content;
  margin-left: 5px;
  font-weight: bold;
  outline: none;
  cursor: pointer;
  padding: 7px 14px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
`;

const EditMediaOverlay = styled.div`
  position: absolute;

  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  &:hover {
  }
`;

const DoneContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  bottom: 10px;
  left: 50%;
  z-index: ${zIndexes('CREATOR_BACKGROUND_DRAWING_SELECTOR_DONE_BUTTON')};
  padding: 0px 5px;
  transform: translateX(-50%);
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.01), 1px 1px 5px rgb(0 0 0 / 12%);
  height: 48px;
`;

const GameDrawingBackgroundSelector = ({
  onSlideChange,
  slide,
  id,
  showDrawingMediaModal,
  setShowDrawingMediaModal,
  hideDropArea,
  className,
  setSelectedElements,
}: {
  onSlideChange: (round: Slide) => void;
  slide: Slide;
  id: string;
  showDrawingMediaModal: boolean;
  setShowDrawingMediaModal: (show: boolean) => void;
  hideDropArea?: boolean;
  className?: string;
  setSelectedElements: (element: SlideElement[] | null) => void;
}) => {
  const { t } = useTranslation('Host');
  const [aspectRatio, setAspectRatio] = useState<number | undefined>(undefined);
  const metrics = useMetrics();
  const [fileToUpload, setFileToUpload] = useState<{
    fileId: string;
    mimeType: string;
    backgroundDrawingFileId?: string;
    backgroundDrawingConfigFiles?: RoundMediaDrawingFile[];
  }>();

  const { tenantId } = useRouterMatch();

  const {
    upload,
    isSuccess,
    isUploading: isUploadingMediaFile,
  } = useGameMediaFileUploader(tenantId);

  const {
    upload: uploadBinaryFiles,
    isUploading: isBinaryFilesUploading,
    isSuccess: isBinaryFilesUploaded,
  } = useGameBackgroundMediaFileUpload(tenantId);
  const {
    upload: uploadJSONFile,
    isSuccess: isJSONFileUploaded,
    isUploading: isJSONFileUploading,
  } = useGameExcalidrawJSONUpload();

  const existingImageElement = useMemo<SlideElementImageType | undefined>(() => {
    const drawingBackgroundContextElement = slide.contextElements.find(
      el => el.purpose === 'drawingBackground',
    );
    if (!drawingBackgroundContextElement) {
      return undefined;
    }
    return slide.elements.find(
      el => el.id === drawingBackgroundContextElement.elementId,
    ) as SlideElementImageType | undefined;
  }, [slide]);

  const existingDrawing = useMemo<ExistingDrawing | undefined>(() => {
    if (existingImageElement) {
      return {
        backgroundDrawingFileId: slide.backgroundDrawingFileId,
        backgroundDrawingConfigFiles: slide.backgroundDrawingConfigFiles,
      };
    }
    return undefined;
  }, [slide, existingImageElement]);
  const getElementsAspectRatio = (elements?: readonly ExcalidrawElement[]) => {
    const height = 1080 / 2;
    const width = 1920 / 2;
    if (elements) {
      const minX = Math.min(...elements.map(e => e.x));
      const maxX = Math.max(...elements.map(e => e.x + e.width));
      const minY = Math.min(...elements.map(e => e.y));
      const maxY = Math.max(...elements.map(e => e.y + e.height));
      const elementWidth = maxX - minX;
      const elementHeight = maxY - minY;
      const elementAspect = elementHeight / elementWidth;
      return elementAspect;
    }
    return height / width;
  };
  const handleDrawingCompleted = (
    imgData?: string,
    elements?: readonly ExcalidrawElement[],
    files?: BinaryFiles,
  ) => {
    const aspectRatio = getElementsAspectRatio(elements);
    setAspectRatio(aspectRatio);
    if (imgData) {
      const fileId = generateUniqueId().toString();
      const jsonFileId = generateUniqueId().toString();

      setFileToUpload({
        fileId,
        mimeType: 'image/jpeg',
        backgroundDrawingFileId: elements ? jsonFileId : undefined,
        backgroundDrawingConfigFiles:
          files &&
          Object.values(files).map(r => ({
            id: r.id,
            mimeType: r.mimeType,
            created: r.created,
          })),
      });

      upload({ imgData, fileId });
      if (elements) {
        const copiedElements = [...elements].map(el => ({ ...el, locked: true }));
        uploadJSONFile({ fileId: jsonFileId, elements: copiedElements });
      }
      if (files) {
        uploadBinaryFiles({ files });
      }
    }
  };

  const isUploading =
    isUploadingMediaFile ||
    (fileToUpload && fileToUpload.backgroundDrawingFileId && isJSONFileUploading) ||
    (fileToUpload && fileToUpload.backgroundDrawingConfigFiles && isBinaryFilesUploading);

  const handleCloseModal = useCallback(() => {
    setShowDrawingMediaModal(false);
  }, [setShowDrawingMediaModal]);

  useEffect(() => {
    if (
      isSuccess &&
      fileToUpload &&
      fileToUpload.backgroundDrawingFileId &&
      isJSONFileUploaded &&
      ((fileToUpload.backgroundDrawingConfigFiles && isBinaryFilesUploaded) ||
        !fileToUpload.backgroundDrawingConfigFiles)
    ) {
      // we adjust the size of the elements based on the excalidraw aspect ratio
      let width = 1920 / 2;
      let height = aspectRatio ? width * aspectRatio : 1080 / 2;
      if (height > 1080 / 2) {
        // scale height down to slide height
        height = 1080 / 2;
        width = aspectRatio ? height / aspectRatio : 1920 / 2;
      }
      const x = 1920 / 2 - width / 2;
      const y = 1080 / 2 - height / 2;
      const imagePreviewElement: SlideElementImageType = {
        type: 'image',
        id: generateUniqueId().toString(),
        angle: 0,
        borderRadius: 0,
        height: height,
        width: width,
        mediaMimetype: 'image/jpeg',
        x: x,
        y: y,
        opacity: 1,
        version: 1,
        imageObjectFit: 'fill',
        mediaFileId: fileToUpload.fileId,
      };

      if (existingImageElement) {
        // we have to make an edit
        onSlideChange({
          ...slide,
          elements: [
            ...slide.elements.filter(e => e.id !== existingImageElement.id),
            imagePreviewElement,
          ],
          contextElements: [
            ...slide.contextElements.filter(
              el => el.elementId !== existingImageElement.id,
            ),
            { elementId: imagePreviewElement.id, purpose: 'drawingBackground' },
          ],
          backgroundDrawingFileId: fileToUpload.backgroundDrawingFileId || null,
          backgroundDrawingConfigFiles:
            fileToUpload.backgroundDrawingConfigFiles &&
            fileToUpload.backgroundDrawingConfigFiles?.length > 0
              ? fileToUpload.backgroundDrawingConfigFiles
              : null,
        });
      } else {
        // We are simply adding a new one
        onSlideChange({
          ...slide,
          elements: [...slide.elements, imagePreviewElement],
          contextElements: [
            ...slide.contextElements,
            { elementId: imagePreviewElement.id, purpose: 'drawingBackground' },
          ],
          backgroundDrawingFileId: fileToUpload.backgroundDrawingFileId || null,
          backgroundDrawingConfigFiles:
            fileToUpload.backgroundDrawingConfigFiles &&
            fileToUpload.backgroundDrawingConfigFiles?.length > 0
              ? fileToUpload.backgroundDrawingConfigFiles
              : null,
        });
      }

      setSelectedElements([imagePreviewElement]);
      setFileToUpload(undefined);
      handleCloseModal();
      setAspectRatio(undefined);
    }
  }, [
    isSuccess,
    fileToUpload,
    slide,
    isBinaryFilesUploaded,
    isJSONFileUploaded,
    onSlideChange,
    handleCloseModal,
    setSelectedElements,
    existingImageElement,
    aspectRatio,
  ]);

  const handleMediaDelete = () => {
    metrics.logEvent('Creator.BackgroundDrawing.Delete', {
      id: id,
      roundId: slide.id,
    });

    if (existingImageElement) {
      onSlideChange({
        ...slide,
        elements: slide.elements.filter(el => el.id !== existingImageElement.id), // todo how to detect the image that is the background? dataQuestionElement perhaps?
        backgroundDrawingFileId: null,
        backgroundDrawingConfigFiles: null,
        contextElements: slide.contextElements.filter(
          el => el.elementId !== existingImageElement.id,
        ),
      });
    }
  };

  const handleEdit = () => {
    metrics.logEvent('Creator.BackgroundDrawing.Edit', { id: id, roundId: slide.id });

    setShowDrawingMediaModal(true);
  };

  const handleFileDropped = (
    files: FileList | null,
    event: DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !SUPPORTED_IMAGE_MIMETYPES.includes(file.type),
      );
      if (invalidFiles.length > 0) {
        metrics.logEvent('Creator.BackgroundDrawing.DropFile.InvalidType', {
          type: invalidFiles[0].type,
          name: invalidFiles[0].name,
          size: invalidFiles[0].size,
        });
        alert(t('Invalid file type, please try again'));
        return;
      }

      if (parsedFiles.length > 0) {
        metrics.logEvent('Creator.BackgroundDrawing.DropFile', {
          count: parsedFiles.length,
          type: parsedFiles[0].type,
          size: parsedFiles[0].size,
          name: parsedFiles[0].name,
        });
        const file = parsedFiles[0];
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          const imgData = reader.result;
          if (imgData) {
            const img = new Image();

            img.src = imgData as string;

            img.onload = function() {
              const imgWidth = img.naturalWidth;
              const imgHeight = img.naturalHeight;

              const id = generateUniqueId().toString();
              const binary: BinaryFileData = {
                created: new Date().getTime(),
                id: id as FileId,
                mimeType: 'image/jpeg',
                dataURL: imgData as DataURL,
              };
              handleDrawingCompleted(
                imgData as string,
                [
                  {
                    angle: 0,
                    backgroundColor: 'transparent',
                    boundElements: null,
                    fileId: id as FileId,
                    fillStyle: 'hachure',
                    groupIds: [],
                    height: imgHeight,
                    id: generateUniqueId().toString(),
                    isDeleted: false,
                    opacity: 100,
                    roughness: 1,
                    scale: [1, 1],
                    seed: 1287592539,
                    status: 'pending',
                    strokeColor: 'transparent',
                    strokeSharpness: 'round',
                    strokeStyle: 'solid',
                    strokeWidth: 1,
                    type: 'image',
                    updated: 1651058325792,
                    version: 4,
                    versionNonce: 960244277,
                    width: imgWidth,
                    x: 0,
                    y: 0,
                    locked: true,
                    link: null,
                  },
                ],
                { [id]: binary },
              );
            };
          }
        });
        reader.readAsDataURL(file);
      }
    }
  };

  return (
    <ConditionalWrapper
      condition={!existingImageElement}
      truthyWrapper={children => (
        <BackgroundContainer>
          <div
            style={{
              height: 'calc(100% - 30px)',
              display: hideDropArea ? 'flex' : 'block',
              justifyContent: hideDropArea ? 'center' : 'start',
              alignItems: hideDropArea ? 'center' : 'start',
            }}
          >
            {children}
          </div>
        </BackgroundContainer>
      )}
    >
      <div>
        {!existingImageElement && !hideDropArea && (
          <FileDropComponent
            onClick={() => setShowDrawingMediaModal(true)}
            onFilesDropped={handleFileDropped}
            customTitle={t(
              'Click to create a background for the participants to draw on',
            )}
            customSupportedFileTypes={t('Supported filetypes are .png and .jpeg')}
          />
        )}
        {!existingImageElement && hideDropArea && (
          <AddMediaButton
            onClick={() => setShowDrawingMediaModal(true)}
            height="40px"
            type="tertiary"
            icon={<AddMediaSvg />}
          >
            Create a background for your participants to draw on
          </AddMediaButton>
        )}
        {existingImageElement && (
          <div style={{ height: 100 }}>
            <MaxSizeMediaContent
              aspectRatio={getMediaAspectRatio(existingImageElement.mediaMimetype)}
            >
              <EditImageOverlay
                className={className}
                onEdit={handleEdit}
                onDelete={handleMediaDelete}
              />
              <GameMediaRenderer
                media={{
                  fileId: existingImageElement.mediaFileId!,
                  fileMimeType: existingImageElement.mediaMimetype,
                  fileAltText: existingImageElement.mediaAltText,
                }}
                style={{ borderRadius: '10px' }}
                isAuthenticated={true}
                isOwner={true}
                isDiscoverTemplate={false}
              />
            </MaxSizeMediaContent>
          </div>
        )}
        <ModalStyled
          visible={showDrawingMediaModal}
          centered
          destroyOnClose
          keyboard={false}
          footer={null}
          closable={false}
          maskClosable={false}
          width={'90%'}
          style={{ paddingBottom: 0 }}
          bodyStyle={{ padding: 0 }}
          onCancel={() => handleCloseModal()}
          onOk={() => handleCloseModal()}
        >
          <ModalContainer>
            <SuspensePostHydration>
              {/* This needs to be wrapped in a PostHydrationProvider and imported dynamic as excalidraw does not work SSR */}
              <DrawingBuilder
                existingDrawing={existingDrawing}
                onDrawingCompleted={handleDrawingCompleted}
                onCancel={() => setShowDrawingMediaModal(false)}
                isUploading={isUploading}
              />
            </SuspensePostHydration>
          </ModalContainer>
        </ModalStyled>
      </div>
    </ConditionalWrapper>
  );
};

const BackgroundContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`;

const AddMediaButton = styled(Button)`
  color: ${theme.colors.blue};
  border: none;
  padding: 0px;
  box-shadow: none;
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 30%;
  height: max-content;
  position: relative;

  svg {
    margin: 0px;
    height: 40px;
    width: 40px;
  }

  &:hover,
  &:focus {
    background: none;
    color: ${theme.colors.chart.p95};
  }

  span {
    display: block;
    width: 100%;
    white-space: break-spaces;
  }
`;

export default GameDrawingBackgroundSelector;

const ModalStyled = styled(Modal)`
  .ant-modal {
    padding-bottom: 0px;
  }
  .ant-modal-content {
    border-radius: 10px;
    box-shadow: none;
    height: 80vh;
  }
  .ant-modal-body {
    box-shadow: none;
    border-radius: 10px;
    background-color: ${theme.colors.white};
    height: 100%;
  }
`;

const ModalContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
  h1 {
    font-weight: bold;
    text-align: center;
    margin-bottom: 20px;
  }

  @media (max-width: 900px) {
    h1 {
      font-size: 18px;
    }
  }
`;
